import { Outlet, Link, createRootRoute, useRouterState } from "@tanstack/react-router";
import { Home, Search, ArrowLeft } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";

import "../styles.css";
import { LanguageProvider, useLang } from "@/i18n/LanguageProvider";
import { AuthProvider } from "@/hooks/useAuth";
import { useTrackVisit } from "@/hooks/useTrackVisit";
import { MaintenanceGate } from "@/components/MaintenanceGate";

const Toaster = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster }))
);

const GoogleOAuthProvider = lazy(() =>
  import("@react-oauth/google").then((m) => ({ default: m.GoogleOAuthProvider }))
);


const GOOGLE_CLIENT_ID =
  "676322085208-np1j8f4ep2e0l2ssui2t72opekj7t2cu.apps.googleusercontent.com";

const GOOGLE_AUTH_PATHS = ["/login", "/signup", "/forgot-password", "/reset-password", "/auth"];

function NotFoundComponent() {
  const { t, dir } = useLang();
  return (
    <div dir={dir} className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-primary/10 via-background to-primary/5 px-4">
      <div className="pointer-events-none absolute -top-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-primary/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 h-[28rem] w-[28rem] rounded-full bg-primary/10 blur-3xl" />
      <div className="relative z-10 w-full max-w-2xl text-center">
        <div className="select-none bg-gradient-to-b from-primary to-primary/40 bg-clip-text text-[9rem] sm:text-[12rem] font-black leading-none text-transparent drop-shadow-sm">
          404
        </div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-foreground">
          {t("notFound.title")}
        </h2>
        <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-md mx-auto">
          {t("notFound.desc")}
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/"
            className="inline-flex h-11 items-center gap-2 rounded-full bg-primary px-6 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/30 transition hover:bg-primary/90"
          >
            <Home className="h-4 w-4" /> {t("notFound.home")}
          </Link>
          <Link
            to="/contact"
            className="inline-flex h-11 items-center gap-2 rounded-full border border-border bg-card px-6 text-sm font-bold text-foreground transition hover:border-primary hover:text-primary"
          >
            <Search className="h-4 w-4" /> {t("notFound.contact")}
          </Link>
          <button
            onClick={() => window.history.back()}
            className="inline-flex h-11 items-center gap-2 rounded-full px-4 text-sm font-medium text-muted-foreground transition hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> {t("notFound.back")}
          </button>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootComponent() {
  useTrackVisit();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const needsGoogle = GOOGLE_AUTH_PATHS.some((p) => pathname.startsWith(p));
  const [showToaster, setShowToaster] = useState(false);

  useEffect(() => {
    const idle = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    const trigger = () => setShowToaster(true);
    const id = idle ? idle(trigger, { timeout: 2500 }) : window.setTimeout(trigger, 1500);
    return () => {
      const cancel = (window as any).cancelIdleCallback as ((id: number) => void) | undefined;
      if (idle && cancel) cancel(id as number);
      else window.clearTimeout(id as number);
    };
  }, []);

  const content = (
    <LanguageProvider>
      <AuthProvider>
        <MaintenanceGate>
          <Outlet />
        </MaintenanceGate>
        {showToaster && (
          <Suspense fallback={null}>
            <Toaster position="top-center" richColors closeButton />
          </Suspense>
        )}
      </AuthProvider>
    </LanguageProvider>
  );

  if (!needsGoogle) return content;
  return (
    <Suspense fallback={content}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{content}</GoogleOAuthProvider>
    </Suspense>
  );
}

